import { JSX, useImperativeHandle, useRef } from 'react';
import './style.scss';
import IMultiFacet from '../../../models/multi-facet.model';
import { MultiFacetType } from '../../../models/multi-facet-type.enum';
import SelectedFilterValue from '../SelectedFilterValue';
import { type IAutocomplete } from '../AutocompleteBase';
import ValidationInfo from '../../../validation/validation-info';
import type { IAutocompleteItem } from '../../../models/autocomplete-item.model';
import MunicipalityListShowcase from '../MunicipalityListShowcase';
import MultiFacetAutocomplete from '../MultiFacetAutocomplete';
import { MultiFacetSelectorLayout } from './multi-facet-selector-layout.enum';
import { MultiFacetHelper } from '../../../helpers/multi-facet.helper';
import { ClientSideStorageItems } from '../../../services/client-side-storage.service';
import LineDescription from '../LineDescription';
import IValidatable, { IServerValidationError } from '../validatable.interface';
import ValidationEngine from '../../../validation/validation-engine';
import { ILine } from '../../../models/line.model';
import { AutocompleteLabelPosition } from '../AutocompleteBase/autocomplete-base.enum';

// === INTERFACES AND ENUMS ===
export interface IMultiFacetSelector extends IValidatable {
  clear: () => void;
}

export interface IMultiFacetSelectorState {
  value: IMultiFacet[];
  validationInfo: ValidationInfo;
}

interface IMultiFacetSelectorProps {
  id: string;
  serverValidationName?: string;
  labelText: string;
  labelPosition?: AutocompleteLabelPosition;
  placeholderText: string;
  multiFacetTypes?: MultiFacetType[];
  required?: boolean;
  noResultsText: string;
  state: IMultiFacetSelectorState;
  layout?: MultiFacetSelectorLayout;
  commonSearches?: IAutocompleteItem[];
  filterLineResults?: (line: ILine) => boolean;
  onChange: (selectedMunicipalities: IMultiFacet[]) => void;
}

function MultiFacetSelector({
  id,
  serverValidationName = undefined,
  labelText,
  labelPosition = AutocompleteLabelPosition.above,
  placeholderText,
  multiFacetTypes = [MultiFacetType.Line, MultiFacetType.Municipality],
  required = false,
  noResultsText,
  state,
  layout = MultiFacetSelectorLayout.vertical,
  commonSearches,
  filterLineResults = undefined,
  onChange,
  ref,
}: IMultiFacetSelectorProps & { ref?: React.Ref<IMultiFacetSelector> }) {
  const multiFacetAutocompleteRef = useRef<IAutocomplete>(null);

  // === INTERFACE IMPLEMENTATION ===
  useImperativeHandle(ref, () => ({
    clear() {
      multiFacetAutocompleteRef.current?.clear();
    },
    checkValidity(): ValidationInfo {
      const updatedValidationInfo = new ValidationInfo(`${labelText} - `);
      ValidationEngine.for(updatedValidationInfo).if(required).notEmpty(state.value).endif().validate();
      return updatedValidationInfo;
    },
    handleServerValidationErrors(errors: IServerValidationError[]): ValidationInfo {
      return new ValidationInfo(
        `${labelText} - `,
        errors
          .filter((e) => serverValidationName && e.name === serverValidationName)
          .map((e) => e.errorMessages)
          .reduce((a, b) => a.concat(b), []),
      );
    },
  }));

  function handleMultiFacetAutocompleteSelect(selectedMultiFacetItem: IMultiFacet | undefined): void {
    if (!selectedMultiFacetItem) {
      return;
    }

    if (state.value.some((m) => MultiFacetHelper.getAutocompleteItemId(m) === MultiFacetHelper.getAutocompleteItemId(selectedMultiFacetItem))) {
      multiFacetAutocompleteRef.current?.clear();
    } else {
      multiFacetAutocompleteRef.current?.addSelectedItemToHistory(selectedMultiFacetItem);
      multiFacetAutocompleteRef.current?.clear();
      onChange([...state.value, selectedMultiFacetItem]);
    }
  }

  function handleSelectedAutocompleteItemRemove(gid: string) {
    onChange(state.value.filter((item) => MultiFacetHelper.getAutocompleteItemId(item) !== gid));
  }

  function getChildShowcase(selectedItem: IMultiFacet): JSX.Element | null {
    if (selectedItem.type === MultiFacetType.Line && selectedItem.line) {
      return <LineDescription line={selectedItem.line} showTransportMode={true} showDirectionName={true} showAppliesFromDateOnFutureLines={true} />;
    } else if (selectedItem.type === MultiFacetType.Municipality && selectedItem.municipality) {
      return <MunicipalityListShowcase municipalities={[selectedItem.municipality!]} />;
    }
    return null;
  }

  return (
    <div className={`multi-facet-selector d-flex flex-column gap-2 ${layout === MultiFacetSelectorLayout.horizontal ? 'flex-sm-row' : ''}`}>
      <div className="multi-facet-selector__autocomplete-wrapper">
        <MultiFacetAutocomplete
          id={id}
          ref={multiFacetAutocompleteRef}
          labelText={labelText}
          labelPosition={labelPosition}
          labelClassName="form-label"
          multiFacetTypes={multiFacetTypes}
          required={required}
          noResultsText={noResultsText}
          placeholderText={placeholderText}
          historyProps={{ localStorageItem: ClientSideStorageItems.LocalStorage.MultiFacetAutocompleteHistory, commonSearches }}
          filterLineResults={filterLineResults}
          state={{
            value: undefined,
            validationInfo: state.validationInfo,
          }}
          onSelect={handleMultiFacetAutocompleteSelect}
        />
      </div>

      {state.value.length > 0 && (
        <div className="multi-facet-selector__selected-items d-flex flex-row flex-wrap gap-2">
          {state.value.map((selectedItem) => {
            const itemId = MultiFacetHelper.getAutocompleteItemId(selectedItem);
            if (!itemId) return null;
            return (
              <SelectedFilterValue
                id={itemId}
                key={itemId}
                ariaLabel={MultiFacetHelper.getAutocompleteItemDisplayName(selectedItem) ?? ''}
                children={getChildShowcase(selectedItem)}
                onRemove={() => handleSelectedAutocompleteItemRemove(itemId)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

export default MultiFacetSelector;
